import React, { useEffect, useState, useRef } from "react";
import { useHistory, Link } from "react-router-dom";
import Slider from "react-slick";
import axios from "axios";
import { toast } from "react-toastify";

// Icons
import {
  FiArrowLeft,
  FiArrowRight,
  FiArrowUpRight,
  FiBriefcase,
  FiCheck,
  FiCheckCircle,
  FiCopy,
  FiHeart,
  FiHome,
  FiLayers,
  FiMapPin,
  FiShare2,
  FiX,
} from "react-icons/fi";
import { FaRupeeSign, FaWhatsapp, FaFacebookF, FaTwitter } from "react-icons/fa";

// Slick Carousel Styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import Login1 from "../auth/Login1";
import SignUp1 from "../auth/SignUp1";

const BRAND = "#700c28";
const BRAND_DARK = "#5a0a20";

const keyframes = `
@keyframes ep-pop {
  0% { transform: scale(1); }
  40% { transform: scale(1.35); }
  100% { transform: scale(1); }
}
@keyframes ep-fade-up {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
}
.ep-pop { animation: ep-pop 0.45s cubic-bezier(0.34, 1.56, 0.64, 1); }
.ep-enter { animation: ep-fade-up 0.6s cubic-bezier(0.22, 1, 0.36, 1) both; }

/* Spotlight that follows the cursor */
.ep-spot { position: absolute; inset: 0; pointer-events: none; opacity: 0; transition: opacity 0.4s ease; }
.group:hover .ep-spot { opacity: 1; }
.ep-spot::before {
  content: "";
  position: absolute;
  left: var(--sx, 50%);
  top: var(--sy, 50%);
  width: 220px;
  height: 220px;
  transform: translate(-50%, -50%);
  background: radial-gradient(circle, rgba(112,12,40,0.14) 0%, rgba(112,12,40,0) 70%);
  border-radius: 9999px;
  transition: left 0.25s ease-out, top 0.25s ease-out;
}

/* Tilt on hover (3D) */
.ep-tilt { transform-style: preserve-3d; transition: transform 0.4s cubic-bezier(0.22,1,0.36,1); }

/* Title underline that grows on hover */
.ep-underline { position: relative; display: inline-block; }
.ep-underline::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -3px;
  height: 2px;
  width: 0;
  background: linear-gradient(to right, #700c28, #b5173f);
  transition: width 0.5s cubic-bezier(0.22,1,0.36,1);
}
.group:hover .ep-underline::after { width: 100%; }

.ep-slider .slick-list { padding: 4px 0 18px; }
.ep-slider .slick-track { display: flex; }
.ep-slider .slick-slide { height: inherit; }
.ep-slider .slick-slide > div { height: 100%; }
`;

const SHARE_LINKS = [
  {
    label: "WhatsApp",
    Icon: FaWhatsapp,
    bg: "bg-emerald-600 hover:bg-emerald-700",
    href: (url) => `https://wa.me/?text=${encodeURIComponent(url)}`,
  },
  {
    label: "Facebook",
    Icon: FaFacebookF,
    bg: "bg-blue-600 hover:bg-blue-700",
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    label: "Twitter",
    Icon: FaTwitter,
    bg: "bg-sky-500 hover:bg-sky-600",
    href: (url) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`,
  },
];

// Compact price: 1,50,000 -> "1.5 L", 2,00,00,000 -> "2 Cr"
const formatCompact = (value) => {
  const price = Number(value);
  if (!price || isNaN(price)) return "";
  if (price >= 10000000) return `${+(price / 10000000).toFixed(1)} Cr`;
  if (price >= 100000) return `${+(price / 100000).toFixed(1)} L`;
  if (price >= 1000) return `${+(price / 1000).toFixed(1)} K`;
  return price.toString();
};

// "2,3" -> ["2 BHK", "3 BHK"]; "2 BHK, 3 BHK" stays as-is
const parseConfigs = (str) => {
  if (!str) return [];
  return str
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => (s.toUpperCase().includes("BHK") ? s : `${s} BHK`));
};

function ProjectCard({ project, index, onHeart, onShare }) {
  const [pulseKey, setPulseKey] = useState(0);
  const cardRef = useRef(null);
  const spotRef = useRef(null);

  const isFav = !!project.is_favorite;
  const units = project.project_properties?.length || 0;

  const prices = (project.project_properties || [])
    .map((p) => Number(p.price))
    .filter((n) => !isNaN(n) && n > 0);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const maxPrice = prices.length ? Math.max(...prices) : null;

  const configs = parseConfigs(project.congfigurations);
  const visibleConfigs = configs.slice(0, 3);
  const extraConfigs = configs.length - visibleConfigs.length;

  // Cursor-following spotlight and 3D tilt
  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    el.style.setProperty("--sx", `${x}px`);
    el.style.setProperty("--sy", `${y}px`);

    const rotateY = ((x / rect.width) - 0.5) * 6;
    const rotateX = ((y / rect.height) - 0.5) * -6;
    el.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-6px)`;
  };

  const handleMouseLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.transform = "";
  };

  const handleHeart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setPulseKey((k) => k + 1);
    onHeart(project);
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onShare(project._id);
  };

  return (
    <div className="h-full px-3  ep-enter" style={{ animationDelay: `${index * 80}ms` }}>
      <Link
        ref={cardRef}
        to={`/projectdetail/${encodeURIComponent(project._id)}`}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        style={{ textDecoration: "none" }}
        className="group ep-tilt relative h-full flex flex-col rounded-2xl bg-white overflow-hidden no-underline hover:no-underline ring-1 ring-black/5 shadow-[0_5px_18px_rgba(17,17,17,0.07)] hover:shadow-[0_20px_45px_rgba(112,12,40,0.2)] transition-shadow duration-500"
      >
        {/* Cursor spotlight overlay */}
        <div ref={spotRef} className="ep-spot z-20" />

        {/* IMAGE */}
        <div className="relative h-48 bg-gray-100">
          <img
            src={project.cover_image || "/image/property-search.jpg"}
            alt={project.project_name || "Project"}
            loading="lazy"
            className="object-cover w-full h-48 transition-transform duration-[900ms] ease-out group-hover:scale-110"
          />

          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-100" />

          {/* Builder badge */}
          <span className="absolute top-3 left-3 inline-flex items-center gap-1 rounded-full bg-white/95 backdrop-blur px-2.5 py-0.5 text-[10px] font-semibold text-[#700c28] shadow-md">
            <FiBriefcase size={11} /> Builder Project
          </span>

          {/* Favorite + share */}
          <div className="absolute top-2.5 right-2.5 flex flex-col gap-1.5">
            <button
              onClick={handleHeart}
              aria-label="Favorite project"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-md transition-all duration-300 hover:scale-110 hover:bg-white active:scale-95"
            >
              <span key={pulseKey} className={`inline-flex ${pulseKey ? "ep-pop" : ""}`}>
                <FiHeart
                  size={13}
                  style={{
                    color: isFav ? BRAND : "#4B5563",
                    fill: isFav ? BRAND : "transparent",
                    transition: "fill 0.3s, color 0.3s",
                  }}
                />
              </span>
            </button>
            <button
              onClick={handleShare}
              aria-label="Share project"
              className="flex h-7 w-7 items-center justify-center rounded-full bg-white/90 backdrop-blur shadow-md text-gray-600 transition-all duration-300 hover:scale-110 hover:text-[#700c28] active:scale-95"
            >
              <FiShare2 size={12} />
            </button>
          </div>

          {/* Project type + units */}
          <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
            {project.project_type ? (
              <span className="inline-flex items-center gap-1 rounded bg-[#700c28] px-2 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white shadow-lg">
                <FiHome size={9} /> {project.project_type}
              </span>
            ) : (
              <span />
            )}
            {units > 0 && (
              <span className="inline-flex items-center gap-1 rounded bg-black/40 backdrop-blur px-2 py-0.5 text-[8px] font-semibold text-white">
                <FiLayers size={9} /> {units} {units === 1 ? "Unit" : "Units"}
              </span>
            )}
          </div>
        </div>

        {/* CONTENT */}
        <div className="relative flex flex-col flex-grow p-4">
          {/* Builder logo */}
          {project.logo && (
            <div className="absolute -top-5 right-4 z-10 h-10 w-10 overflow-hidden rounded-full bg-white shadow-lg ring-2 ring-white transition-transform duration-300 group-hover:scale-110">
              <img src={project.logo} alt="Builder logo" className="h-full w-full object-cover" />
            </div>
          )}

          <h4 className="pr-12 text-base font-bold text-[#1a1a1a] truncate">
            <span className="ep-underline transition-colors duration-300 group-hover:text-[#700c28]">
              {project.project_name || "No Project Name Available"}
            </span>
          </h4>

          <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500 truncate">
            <FiMapPin size={12} className="shrink-0 text-[#700c28]" />
            <span className="truncate">{project.address_area || "No Address Provided"}</span>
          </p>

          {/* Configurations */}
          <div className="mt-3 flex flex-wrap gap-1.5">
            {visibleConfigs.length > 0 ? (
              <>
                {visibleConfigs.map((cfg) => (
                  <span
                    key={cfg}
                    className="rounded-md bg-[#700c28]/5 px-2 py-0.5 text-[10px] font-semibold text-[#700c28] transition-all duration-300 group-hover:bg-[#700c28] group-hover:text-white"
                  >
                    {cfg}
                  </span>
                ))}
                {extraConfigs > 0 && (
                  <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                    +{extraConfigs}
                  </span>
                )}
              </>
            ) : (
              <span className="text-[10px] text-gray-400">No Configurations</span>
            )}
          </div>

          {/* Price + CTA */}
          <div className="mt-auto pt-4">
            <div className="flex items-end justify-between gap-2 border-t border-gray-100 pt-3">
              {minPrice !== null && (
                <div className="min-w-0">
                  <p className="text-[9px] uppercase tracking-widest text-gray-400">
                    {minPrice === maxPrice ? "Price" : "Price range"}
                  </p>
                  <p className="flex items-center gap-0.5 text-sm font-extrabold text-[#700c28] whitespace-nowrap">
                    <FaRupeeSign size={11} />
                    {formatCompact(minPrice)}
                    {minPrice !== maxPrice && (
                      <>
                        <span className="mx-1 text-gray-400">–</span>
                        <FaRupeeSign size={11} />
                        {formatCompact(maxPrice)}
                      </>
                    )}
                  </p>
                </div>
              )}
              <span className="relative flex shrink-0 items-center gap-1 overflow-hidden rounded-lg bg-[#700c28] px-3 py-2 text-xs font-semibold text-white transition-shadow duration-300 group-hover:shadow-md group-hover:shadow-[#700c28]/40">
                <span className="relative z-10">View Project</span>
                <FiArrowUpRight
                  size={11}
                  className="relative z-10 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

const Spotlights = ({
  data,
  openSpotlightShareModal,
  closeShareModal,
  copyLink,
  currentShareUrl,
  fetchHomeData,
}) => {
  const sliderRef = useRef(null);
  const history = useHistory();
  const [projectList, setProjectList] = useState([]);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (data && data.status === 1 && Array.isArray(data.data)) {
      setProjectList(data.data);
    } else {
      setProjectList([]);
    }
  }, [data]);

  const handleClick = () => {
    history.push("/ProjectList");
  };

  const settings = {
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    arrows: false,
    cssEase: "ease-in-out",
    responsive: [
      { breakpoint: 1280, settings: { slidesToShow: 3, slidesToScroll: 1 } },
      { breakpoint: 1024, settings: { slidesToShow: 2, slidesToScroll: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 1, slidesToScroll: 1 } },
    ],
  };

  // Add to favorites
  const addToFavoritesRecommendedProperty = async (projectId) => {
    if (!sessionStorage.getItem("accessToken")) {
      toast.error("Please login to add to favorites");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("user_id", sessionStorage.getItem("accessToken"));
      formData.append("project_id", projectId);

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/cust_api/add_favorite_project`,
        formData
      );

      const favId = res.data?.favorite_id || projectId;
      setProjectList((prev) =>
        prev.map((proj) =>
          proj._id === projectId ? { ...proj, is_favorite: true, favorite_id: favId } : proj
        )
      );
      fetchHomeData();
    } catch (error) {
      toast.error("Failed to save the Project. Please try again.");
    }
  };

  // Remove from favorites
  const removeFromFavoritesRecommendedProperty = async (favoriteId, projectId) => {
    try {
      const formData = new FormData();
      formData.append("favorite_id", favoriteId);

      await axios.delete(`${process.env.REACT_APP_API_URL}/cust_api/remove_favorite_project`, {
        data: formData,
      });

      setProjectList((prev) =>
        prev.map((proj) =>
          proj._id === projectId ? { ...proj, is_favorite: false, favorite_id: null } : proj
        )
      );
      fetchHomeData();
    } catch (error) {
      toast.error("Failed to remove the Project. Please try again.");
    }
  };

  const handleFavoriteToggle = (project) => {
    if (!sessionStorage.getItem("accessToken")) {
      setIsLoginModalOpen(true);
      return;
    }
    if (project.is_favorite) {
      removeFromFavoritesRecommendedProperty(project.favorite_id, project._id);
    } else {
      addToFavoritesRecommendedProperty(project._id);
    }
  };

  const handleCopy = (e) => {
    copyLink(e);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <style>{keyframes}</style>

      {/* Expanded container width to remove wide laptop side gaps */}
      <section className="px-4 sm:px-10  bg-white font-sans overflow-hidden">
        <div className="mx-auto w-full max-w-[1400px]">
          {/* HEADER */}
          <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between px-2 sm:px-0">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#700c28]/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-[#700c28]">
                <FiCheckCircle size={10} /> Verified builders
              </span>
              <h2 className="mt-2 text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1a1a1a]">
                Builders{" "}
                <span className="bg-gradient-to-r from-[#700c28] to-[#b5173f] bg-clip-text text-transparent">
                  Projects
                </span>
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-gray-500">
                Go from browsing to buying with top verified builder projects
              </p>
            </div>

            <button
              onClick={handleClick}
              className="group inline-flex items-center gap-1.5 self-start sm:self-auto rounded-full border-2 border-[#700c28] px-4 py-2 text-xs font-semibold text-[#700c28] transition-all duration-300 hover:bg-[#700c28] hover:text-white hover:shadow-lg hover:shadow-[#700c28]/30 active:scale-95"
            >
              View All Projects
              <FiArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>

          {/* SLIDER WRAPPER */}
          <div className="relative mx-auto w-full px-6 sm:px-10 lg:px-6">
            <button
              aria-label="Previous"
              onClick={() => sliderRef.current?.slickPrev()}
              className="group absolute left-0 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl bg-white text-[#700c28] shadow-xl ring-1 ring-black/5 transition-all duration-300 hover:scale-110 hover:bg-[#700c28] hover:text-white active:scale-90 lg:-left-2"
            >
              <FiArrowLeft size={14} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
            </button>

            <button
              aria-label="Next"
              onClick={() => sliderRef.current?.slickNext()}
              className="group absolute right-0 top-1/2 z-30 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl bg-white text-[#700c28] shadow-xl ring-1 ring-black/5 transition-all duration-300 hover:scale-110 hover:bg-[#700c28] hover:text-white active:scale-90 lg:-right-2"
            >
              <FiArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5" />
            </button>

            {/* SLIDER CONTAINER */}
            <Slider ref={sliderRef} {...settings} className="ep-slider">
              {projectList.map((project, index) => (
                <ProjectCard
                  key={project._id}
                  project={project}
                  index={index}
                  onHeart={handleFavoriteToggle}
                  onShare={(id) => openSpotlightShareModal && openSpotlightShareModal(id)}
                />
              ))}
            </Slider>
          </div>
        </div>
      </section>

      {/* SHARE MODAL */}
      <div
        id="shareModal"
        className="fixed inset-0 z-50 items-center justify-center hidden bg-black/60 backdrop-blur-sm p-4"
      >
        <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-2xl">
          <div className="mb-4 flex items-center justify-between">
            <h5 className="text-base font-bold text-[#1a1a1a]">Share this project</h5>
            <button
              onClick={closeShareModal}
              aria-label="Close"
              className="flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition-all duration-300 hover:rotate-90 hover:bg-gray-100 hover:text-gray-700"
            >
              <FiX size={15} />
            </button>
          </div>

          <div className="mb-5 flex items-center gap-2">
            <input
              type="text"
              className="flex-grow rounded-lg border border-gray-200 p-2 text-xs focus:border-[#700c28] focus:outline-none"
              value={currentShareUrl}
              readOnly
            />
            <button
              onClick={handleCopy}
              className={`flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-medium text-white shadow-sm transition-all duration-300 active:scale-95 ${
                copied ? "bg-emerald-600" : "bg-[#700c28] hover:bg-[#5a0a20]"
              }`}
            >
              {copied ? <FiCheck size={12} /> : <FiCopy size={12} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {SHARE_LINKS.map(({ label, Icon, bg, href }) => (
              <a
                key={label}
                href={href(currentShareUrl)}
                target="_blank"
                rel="noreferrer"
                className={`flex flex-col items-center gap-1 rounded-lg py-2 text-[10px] font-semibold text-white no-underline transition-all duration-300 hover:-translate-y-1 hover:shadow-lg ${bg}`}
              >
                <Icon size={14} />
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <Login1
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSwitchToSignUp={() => {
          setIsLoginModalOpen(false);
          setIsSignUpModalOpen(true);
        }}
      />
      <SignUp1
        isOpen={isSignUpModalOpen}
        onClose={() => setIsSignUpModalOpen(false)}
        onSwitchToLogin={() => {
          setIsSignUpModalOpen(false);
          setIsLoginModalOpen(true);
        }}
      />
    </>
  );
};

export default Spotlights;